/**
 * VehicleDetail.jsx — Page de détail d'un véhicule
 *
 * Charge les données du véhicule depuis l'API (/api/products/:id),
 * affiche une galerie d'images avec swipe mobile, un formulaire de
 * réservation avec calcul de prix en temps réel, et une section agence
 * cliquable pour aller sur le profil de l'agence.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
	FiArrowLeft,
	FiCheckCircle,
	FiDroplet,
	FiMapPin,
	FiNavigation,
	FiTool,
	FiUsers,
	FiPhone,
	FiTrendingUp,
	FiChevronLeft,
	FiChevronRight,
	FiCalendar,
	FiHash,
	FiEye,
	FiStar,
	FiShield,
} from 'react-icons/fi';
import {
	calculateTotalPrice,
	getVehicleGallery,
	OPTION_PRICES
} from './VehicleDetail.js';
import { getApiBaseUrl } from '../../utils/apiBaseUrl';
import { MOROCCAN_CITIES } from '../../utils/moroccanCities';
import BottomNav from '../shared/BottomNav.jsx';
import BookingDocModal from '../shared/BookingDocModal.jsx';
import './VehicleDetail.css';

/** Affiche 5 étoiles colorées selon la note (arrondie à l'entier le plus proche) */
function renderStars(rating) {
	return Array.from({ length: 5 }, (_, index) => (
		<span key={`${rating}-${index}`} className={`ux-vd-star ${index < Math.round(rating) ? 'is-filled' : ''}`}>
			<FiStar />
		</span>
	));
}

/** Formate un prix en DH : 1500 → "1 500 DH" */
function formatCurrency(value) {
	return `${Number(value || 0).toLocaleString('fr-FR')} DH`;
}

/** Formate un kilométrage : 12000 → "12 000 km" */
function formatMileage(value) {
	if (!value && value !== 0) return '—';
	return `${Number(value).toLocaleString('fr-FR')} km`;
}

/**
 * createListingPlaceholder — Génère un SVG inline comme image de fallback.
 * Utilisé quand le véhicule n'a pas d'image uploadée.
 */
function createListingPlaceholder(label) {
	const safeLabel = String(label || 'Vehicule')
		.replace(/&/g, '&amp;')
		.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;');

	const svg = `
		<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1200 700" role="img" aria-label="${safeLabel}">
			<defs>
				<linearGradient id="bg" x1="0" y1="0" x2="1" y2="1">
					<stop offset="0%" stop-color="#0f172a" />
					<stop offset="100%" stop-color="#1d4ed8" />
				</linearGradient>
			</defs>
			<rect width="1200" height="700" rx="36" fill="url(#bg)" />
			<text x="50%" y="52%" text-anchor="middle" font-family="Montserrat, Arial, sans-serif" font-size="64" font-weight="700" fill="#f7fbff">${safeLabel}</text>
			<text x="50%" y="62%" text-anchor="middle" font-family="Rajdhani, Arial, sans-serif" font-size="28" letter-spacing="6" fill="rgba(255,255,255,0.78)">ULTIMATEX DRIVE</text>
		</svg>
	`;

	return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
}

/**
 * mapProductToVehicle — Normalise un produit MongoDB en objet véhicule uniforme.
 * Gère les différences de nommage entre versions de l'API (ex: agencyName vs agence).
 */
function mapProductToVehicle(product) {
	if (!product) return null;

	const galleryImages = Array.isArray(product.galleryImages) ? product.galleryImages.filter(Boolean) : [];
	const vehicleName = product.name || [product.vehicleBrand, product.vehicleModel].filter(Boolean).join(' ').trim() || 'Vehicule';
	const image = product.mainImageUrl || galleryImages[0] || createListingPlaceholder(vehicleName);

	return {
		id:               product._id || product.id,
		nom:              vehicleName,
		agence:           product.agencyName || 'Agence',
		agencyId:         product.agencyId || '',
		ville:            product.city || '',
		type:             product.vehicleType || 'Vehicule',
		vehicleType:      product.vehicleType || '',
		vehicleBrand:     product.vehicleBrand || '',
		vehicleModel:     product.vehicleModel || '',
		vehicleYear:      product.vehicleYear || null,
		transmission:     product.transmission || '',
		carburant:        product.fuelType || '',
		fuelType:         product.fuelType || '',
		places:           Number(product.seats || 0),
		seats:            Number(product.seats || 0),
		prixJour:         Number(product.price || 0),
		price:            Number(product.price || 0),
		// disponible = isAvailable ET stock > 0
		disponible:       product.isAvailable !== false && Number(product.stock || 0) > 0,
		isAvailable:      product.isAvailable !== false,
		stock:            Number(product.stock || 0),
		note:             Number(product.note || 0),
		options:          Array.isArray(product.features) ? product.features : [],
		features:         Array.isArray(product.features) ? product.features : [],
		deliveryAvailable: product.deliveryAvailable === true,
		deliveryPrice:    Number(product.deliveryPrice || 0),
		driverAvailable:  product.driverAvailable === true,
		driverPrice:      Number(product.driverPrice || 0),
		description:      product.description || '',
		mileage:          Number(product.mileage || 0),
		color:            product.color || '',
		registrationNumber: product.registrationNumber || '',
		image,
		galleryImages
	};
}

function VehicleDetail() {
	const { id } = useParams();
	const navigate = useNavigate();
	const apiBaseUrl = getApiBaseUrl();

	// ── État du composant ────────────────────────────────────────────────────
	const [vehicle, setVehicle] = useState(null);
	const [isLoading, setIsLoading] = useState(true);
	const [error, setError] = useState('');
	const [agencyVehiclesCount, setAgencyVehiclesCount] = useState(0);

	// ── État galerie ──────────────────────────────────────────────────────────
	const [selectedImage, setSelectedImage] = useState(0);
	const touchStartX = useRef(null);
	const galleryRef = useRef(null);

	// ── État formulaire de réservation ────────────────────────────────────────
	const [startDate, setStartDate] = useState('');
	const [endDate, setEndDate] = useState('');
	const [pickupLocation, setPickupLocation] = useState('');
	const [options, setOptions] = useState({ gps: false, babySeat: false, premiumInsurance: false });

	// ── Documents obligatoires (photos sur place + permis) ────────────────────
	const [showDocModal, setShowDocModal] = useState(false);

	// gallery et priceSummary recalculés uniquement quand les dépendances changent
	const gallery = useMemo(() => getVehicleGallery(vehicle), [vehicle]);
	const priceSummary = useMemo(
		() => calculateTotalPrice(vehicle?.prixJour || 0, startDate, endDate, options),
		[vehicle?.prixJour, startDate, endDate, options]
	);

	// Scroll en haut à chaque changement d'ID
	useEffect(() => {
		window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
	}, [id]);

	/**
	 * Chargement du véhicule depuis l'API.
	 * Le flag `active` empêche les mises à jour d'état si le composant
	 * est démonté avant la fin de la requête (race condition).
	 */
	useEffect(() => {
		let active = true;

		const loadVehicle = async () => {
			setIsLoading(true);
			setError('');

			try {
				const response = await fetch(`${apiBaseUrl}/api/products/${id}`);
				const data = await response.json();

				if (!response.ok) {
					throw new Error(data?.message || 'Le véhicule demandé n\'existe pas ou n\'est plus disponible.');
				}
				if (!active) return;

				const normalizedVehicle = mapProductToVehicle(data);
				setVehicle(normalizedVehicle);
				setPickupLocation((previous) => previous || normalizedVehicle?.ville || '');

				// Chargement du nombre d'annonces de l'agence (affiché dans la section agence)
				if (normalizedVehicle?.agencyId) {
					try {
						const agencyResponse = await fetch(`${apiBaseUrl}/api/products?agencyId=${encodeURIComponent(normalizedVehicle.agencyId)}`);
						const agencyData = await agencyResponse.json();
						if (agencyResponse.ok && active) {
							setAgencyVehiclesCount(Array.isArray(agencyData) ? agencyData.length : 0);
						}
					} catch {
						if (active) setAgencyVehiclesCount(0);
					}
				}
			} catch (fetchError) {
				if (!active) return;
				setVehicle(null);
				setError(fetchError.message || 'Le véhicule demandé n\'existe pas ou n\'est plus disponible.');
			} finally {
				if (active) setIsLoading(false);
			}
		};

		loadVehicle();
		return () => { active = false; }; // cleanup : désactive les setState après démontage
	}, [apiBaseUrl, id]);

	// ── Gestion du swipe tactile (galerie mobile) ─────────────────────────────
	const handleSwipeStart = (e) => { touchStartX.current = e.touches[0].clientX; };
	const handleSwipeEnd = (e) => {
		if (touchStartX.current === null) return;
		const diff = touchStartX.current - e.changedTouches[0].clientX;
		if (Math.abs(diff) > 40) {
			// Swipe gauche → image suivante, swipe droit → image précédente
			if (diff > 0) setSelectedImage((prev) => (prev + 1) % gallery.length);
			else setSelectedImage((prev) => (prev - 1 + gallery.length) % gallery.length);
		}
		touchStartX.current = null;
	};

	// ── États de chargement et d'erreur ──────────────────────────────────────
	if (isLoading) {
		return (
			<div className="ux-vd-page">
				<main className="ux-vd-main">
					<div className="ux-vd-loading">
						<div className="ux-vd-loading-spinner" />
						<p>Chargement du véhicule...</p>
					</div>
				</main>
				<BottomNav />
			</div>
		);
	}

	if (!vehicle) {
		return (
			<div className="ux-vd-page">
				<main className="ux-vd-main">
					<article className="ux-vd-not-found">
						<h1>Véhicule introuvable</h1>
						<p>{error || 'Le véhicule demandé n\'existe pas ou n\'est plus disponible.'}</p>
						<Link to="/search" className="ux-btn ux-btn-primary">
							Retour à la recherche
						</Link>
					</article>
				</main>
				<BottomNav />
			</div>
		);
	}


	/** Ouvre le modal de documents avant de passer au paiement */
	const handleProceedToPayment = () => {
		setShowDocModal(true);
	};

	/** Appelé par BookingDocModal une fois tous les documents validés */
	const handleDocComplete = async ({ permisRecto, permisVerso } = {}) => {
		setShowDocModal(false);

		let customerName = '';
		let customerEmail = '';
		let customerId = null;
		try {
			const authRaw = localStorage.getItem('exp-auth-user');
			const profileRaw = localStorage.getItem('exp-user-profile');
			const auth = authRaw ? JSON.parse(authRaw) : null;
			const profile = profileRaw ? JSON.parse(profileRaw) : null;
			const src = profile || auth;
			if (src) {
				const first = src.firstName || src.prenom || src.name || '';
				const last = src.lastName || src.nom || '';
				customerName = [first, last].filter(Boolean).join(' ').trim();
				customerEmail = src.email || '';
				customerId = src._id || src.id || null;
			}
		} catch (_) { /* localStorage indisponible */ }

		// Upload des permis vers Cloudinary
		let licenseUrls = [];
		try {
			const files = [permisRecto, permisVerso].filter(Boolean);
			if (files.length > 0) {
				const formData = new FormData();
				files.forEach(f => formData.append('files', f));
				formData.append('folder', 'licenses');
				const uploadRes = await fetch(`${apiBaseUrl}/api/upload`, {
					method: 'POST',
					body: formData,
				});
				if (uploadRes.ok) {
					const uploadData = await uploadRes.json();
					licenseUrls = (uploadData.files || []).map(f => f.url);
				}
			}
		} catch (_) { /* l'échec de l'upload ne bloque pas le paiement */ }

		navigate('/payement', {
			state: {
				listingId: id,
				vehicleName: vehicle.nom,
				agencyName: vehicle.agence,
				agencyId: vehicle.agencyId || vehicle.agenceId || null,
				customerName,
				customerEmail,
				customerId,
				ville: vehicle.ville,
				startDate,
				endDate,
				pickupLocation,
				options,
				priceSummary,
				totalPrice: priceSummary.total,
				prixJour: vehicle.prixJour,
				vehiclePhotoUrl: vehicle.image || vehicle.galleryImages?.[0] || null,
				licenseUrls,
			}
		});
	};

	/**
	 * openChat — Crée ou ouvre une discussion avec l'agence.
	 * Une clé de conversation unique est générée depuis l'ID du véhicule,
	 * de l'agence et du client pour retrouver le même fil à chaque fois.
	 */
	const openChat = async (contextType = 'agency') => {
		try {
			const authRaw = window.localStorage.getItem('exp-auth-user');
			const profileRaw = window.localStorage.getItem('exp-user-profile');
			const user = (authRaw ? JSON.parse(authRaw) : null) || (profileRaw ? JSON.parse(profileRaw) : null);
			const customerId = user?.id || user?._id || null;
			const firstName = user?.firstName || user?.prenom || '';
			const lastName = user?.lastName || user?.nom || '';
			const customerName = [firstName, lastName].filter(Boolean).join(' ').trim() || user?.email || 'Client';
			const agencyKey = vehicle.agencyId || vehicle.agence;
			// Clé unique pour retrouver la même conversation (format : type|listing|agence|client)
			const conversationKey = `${contextType}|${String(id || vehicle.nom).toLowerCase()}|${String(agencyKey).toLowerCase()}|${String(customerId || customerName).toLowerCase()}`;
			const res = await fetch(`${apiBaseUrl}/api/discussions`, {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({ conversationKey, contextType, listingId: id, vehicleName: vehicle.nom, agencyId: vehicle.agencyId, agencyName: vehicle.agence, customerId, customerName })
			});
			const data = await res.json();
			navigate('/messages', { state: { selectDiscussionId: data._id } });
		} catch {
			navigate('/messages');
		}
	};

	// Tableau de fiches techniques pour la grille de spécifications
	const features = vehicle.features || vehicle.options || [];
	const infoCards = [
		{ label: 'Marque',        value: vehicle.vehicleBrand    || '—', icon: FiNavigation },
		{ label: 'Modèle',        value: vehicle.vehicleModel    || '—', icon: FiHash },
		{ label: 'Année',         value: vehicle.vehicleYear     || '—', icon: FiCalendar },
		{ label: 'Type',          value: vehicle.vehicleType     || '—', icon: FiTool },
		{ label: 'Transmission',  value: vehicle.transmission    || '—', icon: FiTool },
		{ label: 'Carburant',     value: vehicle.fuelType        || '—', icon: FiDroplet },
		{ label: 'Places',        value: vehicle.seats           || '—', icon: FiUsers },
		{ label: 'Kilométrage',   value: formatMileage(vehicle.mileage), icon: FiNavigation },
		{ label: 'Couleur',       value: vehicle.color           || '—', icon: FiEye },
		{ label: 'Immatriculation', value: vehicle.registrationNumber || '—', icon: FiHash },
		{ label: 'Stock',         value: vehicle.stock           || '0', icon: FiTrendingUp },
		{ label: 'Ville',         value: vehicle.ville           || '—', icon: FiMapPin }
	];

	return (
		<div className="ux-vd-page">
			<main className="ux-vd-main">

				{/* ── Barre de navigation haut ── */}
				<div className="ux-vd-top-bar">
					<button
						type="button"
						className="ux-vd-back-btn"
						onClick={() => navigate(-1)}
						aria-label="Revenir en arrière"
					>
						<FiArrowLeft size={16} />
						<span>Retour</span>
					</button>
				</div>

				{/* ── Section héro : galerie + carte résumé ── */}
				<section className="ux-vd-hero" aria-label="Détail véhicule">

					{/* Galerie d'images avec swipe et miniatures */}
					<div className="ux-vd-gallery-wrap">
						<div
							className="ux-vd-main-image"
							ref={galleryRef}
							onTouchStart={handleSwipeStart}
							onTouchEnd={handleSwipeEnd}
						>
							<img src={gallery[selectedImage]} alt={`${vehicle.nom} — vue ${selectedImage + 1}`} />

							{/* Flèches de navigation (visibles si plus d'une image) */}
							{gallery.length > 1 && (
								<>
									<button
										type="button"
										className="ux-vd-gallery-nav ux-vd-gallery-nav-prev"
										onClick={() => setSelectedImage((prev) => (prev - 1 + gallery.length) % gallery.length)}
										aria-label="Image précédente"
									>
										<FiChevronLeft size={20} />
									</button>
									<button
										type="button"
										className="ux-vd-gallery-nav ux-vd-gallery-nav-next"
										onClick={() => setSelectedImage((prev) => (prev + 1) % gallery.length)}
										aria-label="Image suivante"
									>
										<FiChevronRight size={20} />
									</button>
								</>
							)}

							{/* Compteur d'images */}
							<div className="ux-vd-img-counter">
								{selectedImage + 1} / {gallery.length}
							</div>

							{/* Badge "Disponible" */}
							{vehicle.isAvailable && (
								<div className="ux-vd-avail-badge">
									<span className="ux-vd-avail-dot" />
									Disponible
								</div>
							)}
						</div>

						{/* Miniatures cliquables */}
						{gallery.length > 1 && (
							<div className="ux-vd-thumbs">
								{gallery.map((image, index) => (
									<button
										type="button"
										key={`thumb-${index}`}
										className={`ux-vd-thumb ${selectedImage === index ? 'is-active' : ''}`}
										onClick={() => setSelectedImage(index)}
										aria-label={`Vue ${index + 1}`}
									>
										<img src={image} alt={`${vehicle.nom} vue ${index + 1}`} />
									</button>
								))}
							</div>
						)}

						{/* Points indicateurs (mobile) */}
						{gallery.length > 1 && (
							<div className="ux-vd-dots" aria-hidden="true">
								{gallery.map((_, index) => (
									<button
										key={index}
										type="button"
										className={`ux-vd-dot ${selectedImage === index ? 'is-active' : ''}`}
										onClick={() => setSelectedImage(index)}
									/>
								))}
							</div>
						)}
					</div>

					{/* Carte résumé : prix, badges, boutons Réserver / Contacter */}
					<article className="ux-vd-summary-card">
						<div className="ux-vd-summary-header">
							<div>
								<p className="ux-vd-subtitle">
									{[vehicle.vehicleBrand, vehicle.vehicleModel, vehicle.vehicleYear].filter(Boolean).join(' · ')}
								</p>
								<h1>{vehicle.nom}</h1>
							</div>
							{vehicle.note > 0 && (
								<div className="ux-vd-rating">
									<div className="ux-vd-stars">{renderStars(vehicle.note)}</div>
									<span>{Number(vehicle.note).toFixed(1)}</span>
								</div>
							)}
						</div>

						<div className="ux-vd-badges">
							{vehicle.vehicleType  && <span className="ux-vd-badge">{vehicle.vehicleType}</span>}
							{vehicle.transmission && <span className="ux-vd-badge">{vehicle.transmission}</span>}
							{vehicle.fuelType     && <span className="ux-vd-badge">{vehicle.fuelType}</span>}
							{vehicle.ville && (
								<span className="ux-vd-badge ux-vd-badge-location">
									<FiMapPin size={10} /> {vehicle.ville}
								</span>
							)}
						</div>

						<div className="ux-vd-price-block">
							<div className="ux-vd-price-day">
								{formatCurrency(vehicle.prixJour)}
								<span className="ux-vd-price-unit"> / jour</span>
							</div>
							{/* Total estimé affiché uniquement quand des dates sont sélectionnées */}
							{priceSummary.rentalDays > 0 && (
								<div className="ux-vd-price-total">
									<span>Total estimé ({priceSummary.rentalDays}j)</span>
									<strong>{formatCurrency(priceSummary.total)}</strong>
								</div>
							)}
						</div>

						<div className="ux-vd-hero-actions">
							<button type="button" className="ux-btn ux-btn-primary" onClick={handleProceedToPayment}>
								<FiCalendar size={14} />
								Réserver
							</button>
							<button type="button" className="ux-btn ux-btn-ghost" onClick={() => openChat('agency')}>
								<FiPhone size={14} />
								Contacter
							</button>
						</div>
					</article>
				</section>

				{/* ── Fiche technique complète ── */}
				<section className="ux-vd-panel" aria-labelledby="details-title">
					<div className="ux-vd-section-head">
						<div>
							<p className="ux-vd-kicker">Tout ce que l'agence a renseigné</p>
							<h2 id="details-title">Fiche complète du véhicule</h2>
						</div>
					</div>
					<div className="ux-vd-specs-grid ux-vd-specs-grid--dense">
						{infoCards.map(({ label, value, icon: Icon }) => (
							<div className="ux-vd-spec-item" key={label}>
								<Icon size={16} />
								<div>
									<span>{label}</span>
									<strong title={String(value)}>{value}</strong>
								</div>
							</div>
						))}
					</div>
				</section>

				{/* ── Description ── */}
				{vehicle.description && (
					<section className="ux-vd-panel" aria-labelledby="desc-title">
						<h2 id="desc-title">Description</h2>
						<div className="ux-vd-description">{vehicle.description}</div>
					</section>
				)}

				{/* ── Équipements inclus ── */}
				{features.length > 0 && (
					<section className="ux-vd-panel" aria-labelledby="features-title">
						<h2 id="features-title">Équipements inclus</h2>
						<div className="ux-vd-features-list">
							{features.map((feature) => (
								<span key={feature} className="ux-vd-feature-tag">
									<FiCheckCircle size={11} />
									{feature}
								</span>
							))}
						</div>
					</section>
				)}

				{/* ── Services disponibles (livraison, chauffeur) ── */}
				{(vehicle.deliveryAvailable || vehicle.driverAvailable) && (
					<section className="ux-vd-panel" aria-labelledby="services-title">
						<h2 id="services-title">Services disponibles</h2>
						<div className="ux-vd-services-list">
							{vehicle.deliveryAvailable && (
								<span className="ux-vd-service-tag ux-vd-service-tag--delivery">
									<FiCheckCircle size={11} />
									Livraison à domicile
									{vehicle.deliveryPrice > 0 && (
										<strong className="ux-vd-service-price">{vehicle.deliveryPrice.toLocaleString('fr-FR')} DH</strong>
									)}
								</span>
							)}
							{vehicle.driverAvailable && (
								<span className="ux-vd-service-tag ux-vd-service-tag--driver">
									<FiCheckCircle size={11} />
									Chauffeur disponible
									{vehicle.driverPrice > 0 && (
										<strong className="ux-vd-service-price">{vehicle.driverPrice.toLocaleString('fr-FR')} DH / jour</strong>
									)}
								</span>
							)}
						</div>
					</section>
				)}

				{/* ── Section agence partenaire (cliquable → profil agence) ── */}
				<section className="ux-vd-panel" aria-labelledby="agency-title">
					<div className="ux-vd-section-head">
						<div>
							<p className="ux-vd-kicker">Agence</p>
							<h2 id="agency-title">Agence partenaire</h2>
						</div>
					</div>
					<div className="ux-vd-agency-card">
						{/* Zone cliquable qui navigue vers /agency/:agencyName */}
						<div
							className="ux-vd-agency-top ux-vd-agency-top--clickable"
							role="link"
							tabIndex={0}
							onClick={() => navigate(`/agency/${encodeURIComponent(vehicle.agence)}`)}
							onKeyDown={(e) => e.key === 'Enter' && navigate(`/agency/${encodeURIComponent(vehicle.agence)}`)}
							title={`Voir le profil de ${vehicle.agence}`}
						>
							{/* Logo : initiales de l'agence (2 premiers mots) */}
							<div className="ux-vd-agency-logo">
								{vehicle.agence.split(' ').map((w) => w[0]).join('').slice(0, 2).toUpperCase()}
							</div>
							<div className="ux-vd-agency-info">
								<h3 className="ux-vd-agency-name-link">{vehicle.agence}</h3>
								<p><FiMapPin size={12} /> {vehicle.ville}, Maroc</p>
								<p className="ux-vd-agency-count">
									<FiCheckCircle size={12} /> {agencyVehiclesCount} annonce(s)
								</p>
							</div>
						</div>

						<div className="ux-vd-agency-note">
							<FiShield size={14} />
							<span>Contactez l'agence directement depuis le site pour garder la réservation et les avantages de la plateforme.</span>
						</div>

						<div className="ux-vd-agency-actions">
							<button type="button" className="ux-btn ux-btn-primary" onClick={() => openChat('agency')}>
								Discuter avec l'agence
							</button>
							<button
								type="button"
								className="ux-btn ux-btn-ghost"
								onClick={() => navigate(`/agency/${encodeURIComponent(vehicle.agence)}`)}
							>
								Voir le profil
							</button>
						</div>
					</div>
				</section>

				{/* ── Formulaire de réservation rapide ── */}
				<section className="ux-vd-panel" aria-labelledby="booking-title">
					<div className="ux-vd-section-head">
						<div>
							<p className="ux-vd-kicker">Réservation</p>
							<h2 id="booking-title">Réservation rapide</h2>
						</div>
					</div>
					<div className="ux-vd-booking-card">
						<div className="ux-vd-booking-grid">
							<label className="ux-vd-field">
								<span>Date de début</span>
								<input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
							</label>
							<label className="ux-vd-field">
								<span>Date de fin</span>
								<input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
							</label>
							<label className="ux-vd-field ux-vd-field-full">
								<span>Lieu de récupération</span>
								<select
									value={pickupLocation}
									onChange={(e) => setPickupLocation(e.target.value)}
								>
									<option value="">Sélectionner une ville...</option>
									{MOROCCAN_CITIES.map((city) => (
										<option key={city} value={city}>{city}</option>
									))}
								</select>
							</label>
						</div>

						{/* Options supplémentaires (prix définis dans VehicleDetail.js) */}
						<div className="ux-vd-options">
							<p className="ux-vd-options-title">Options supplémentaires</p>
							<div className="ux-vd-options-grid">
								<label className="ux-vd-option-toggle">
									<input
										type="checkbox"
										checked={options.gps}
										onChange={(e) => setOptions((prev) => ({ ...prev, gps: e.target.checked }))}
									/>
									<span className="ux-vd-option-box">
										<FiNavigation size={14} />
										<span>GPS</span>
										<em>+{OPTION_PRICES.gps} DH/j</em>
									</span>
								</label>
								<label className="ux-vd-option-toggle">
									<input
										type="checkbox"
										checked={options.babySeat}
										onChange={(e) => setOptions((prev) => ({ ...prev, babySeat: e.target.checked }))}
									/>
									<span className="ux-vd-option-box">
										<FiUsers size={14} />
										<span>Siège bébé</span>
										<em>+{OPTION_PRICES.babySeat} DH/j</em>
									</span>
								</label>
								<label className="ux-vd-option-toggle">
									<input
										type="checkbox"
										checked={options.premiumInsurance}
										onChange={(e) => setOptions((prev) => ({ ...prev, premiumInsurance: e.target.checked }))}
									/>
									<span className="ux-vd-option-box">
										<FiShield size={14} />
										<span>Assurance premium</span>
										<em>+{OPTION_PRICES.premiumInsurance} DH/j</em>
									</span>
								</label>
							</div>
						</div>

						{/* Récapitulatif de prix en temps réel (aria-live = annoncé aux lecteurs d'écran) */}
						<div className="ux-vd-price-breakdown" aria-live="polite">
							<div className="ux-vd-breakdown-row">
								<span>Location ({priceSummary.rentalDays} jour(s))</span>
								<span>{formatCurrency(priceSummary.baseTotal)}</span>
							</div>
							<div className="ux-vd-breakdown-row">
								<span>Options</span>
								<span>{formatCurrency(priceSummary.optionsTotal)}</span>
							</div>
							<div className="ux-vd-breakdown-total">
								<span>Total</span>
								<strong>{formatCurrency(priceSummary.total)}</strong>
							</div>
						</div>

						<button
							type="button"
							className="ux-btn ux-btn-primary ux-vd-booking-submit"
							onClick={handleProceedToPayment}
						>
							<FiCalendar size={15} />
							Confirmer la réservation
						</button>
					</div>
				</section>

			</main>

			{/* ── Barre sticky mobile : prix + boutons rapides ── */}
			<div className="ux-vd-sticky-bar">
				<div className="ux-vd-sticky-price">
					<strong>{formatCurrency(vehicle.prixJour)}</strong>
					<span>/ jour</span>
				</div>
				<div className="ux-vd-sticky-actions">
					<button type="button" className="ux-btn ux-btn-ghost ux-vd-sticky-chat" onClick={() => openChat('agency')}>
						<FiPhone size={16} />
					</button>
					<button type="button" className="ux-btn ux-btn-primary ux-vd-sticky-book" onClick={handleProceedToPayment}>
						Réserver maintenant
					</button>
				</div>
			</div>

			<BottomNav />

			{showDocModal && (
				<BookingDocModal
					onComplete={handleDocComplete}
					onClose={() => setShowDocModal(false)}
				/>
			)}
		</div>
	);
}

export default VehicleDetail;
